import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { isBlank } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BasicDropdown from 'ember-basic-dropdown/components/basic-dropdown';
import { pluralize } from 'ember-inflector';
import UiInput from 'garaje/components/ui-input';

interface EventLogDropdownFilterSignature {
  Args: {
    options: string[];
    selected: string[];
    placeholder: string;
    allSelectedText?: string;
    onChange: (selected: string[]) => void;
    isDisabled?: boolean;
  };
  Element: HTMLElement;
}

export default class EventLogDropdownFilter extends Component<EventLogDropdownFilterSignature> {
  @tracked searchQuery = '';

  get selectedText(): string {
    const totalSelected = this.args.selected.length;
    const totalOptions = this.args.options.length;

    if (totalSelected === 0) {
      return this.args.placeholder;
    } else if (totalSelected === totalOptions && this.args.allSelectedText) {
      return this.args.allSelectedText;
    } else if (totalSelected === 1) {
      return `1 ${this.args.placeholder.toLowerCase()}`;
    } else {
      const pluralizedName = pluralize(this.args.placeholder.toLowerCase());
      return `${totalSelected} ${pluralizedName}`;
    }
  }

  get filteredOptions(): Array<{ value: string; isSelected: boolean }> {
    const options = isBlank(this.searchQuery)
      ? this.args.options
      : this.args.options.filter((option) => option.toLowerCase().includes(this.searchQuery.toLowerCase()));

    return options.map((option) => ({
      value: option,
      isSelected: this.args.selected.includes(option),
    }));
  }

  get allSelected(): boolean {
    return this.args.options.length > 0 && this.args.selected.length === this.args.options.length;
  }

  get noneSelected(): boolean {
    return this.args.selected.length === 0;
  }

  setSearchQuery(value: string): void {
    this.searchQuery = value;
  }

  @action
  handleSearch(value: string): void {
    this.setSearchQuery(value);
  }

  @action
  onSelectAll(): void {
    const unselected = this.args.options.filter((option) => !this.args.selected.includes(option));
    this.args.onChange([...this.args.selected, ...unselected]);
  }

  @action
  onDeselectAll(): void {
    this.args.onChange([]);
  }

  @action
  onOptionToggle(option: string): void {
    const isSelected = this.args.selected.includes(option);

    if (isSelected) {
      this.args.onChange(this.args.selected.filter((selected) => selected !== option));
    } else {
      this.args.onChange([...this.args.selected, option]);
    }
  }

  <template>
    <BasicDropdown @disabled={{@isDisabled}} ...attributes as |dropdown|>
      <dropdown.Trigger class="ember-power-select-trigger !mb-0" data-test-filter="{{@placeholder}}">
        <div data-test-selected-option class="ember-power-select-selected-item">
          <div class="overflow-x-hidden whitespace-nowrap truncate">
            {{this.selectedText}}
          </div>
        </div>
      </dropdown.Trigger>
      <dropdown.Content>
        <div
          class="ember-basic-dropdown-content ember-power-select-dropdown ember-basic-dropdown-content--left ember-basic-dropdown-content--below ember-basic-dropdown--transitioned-in w-[280px]"
        >
          <div class="ember-power-select-search">
            <UiInput
              data-test-search-box
              class="ember-power-select-search-input w-full"
              @type="search"
              @value={{this.searchQuery}}
              @update={{this.handleSearch}}
            />
          </div>

          {{#if this.filteredOptions.length}}
            <div class="px-4 py-1">
              {{#if this.allSelected}}
                <button
                  data-test-deselect-all
                  {{on "click" this.onDeselectAll}}
                  class="btn btn--link !text-gem-60 btn--small font-bold"
                  type="button"
                >
                  Deselect all
                </button>
              {{else if this.noneSelected}}
                <button
                  data-test-select-all
                  {{on "click" this.onSelectAll}}
                  class="btn btn--link !text-gem-60 btn--small font-bold"
                  type="button"
                >
                  Select all
                </button>
              {{else}}
                <button
                  data-test-select-all
                  {{on "click" this.onSelectAll}}
                  class="btn btn--link !text-gem-60 btn--small font-bold"
                  type="button"
                >
                  Select all
                </button>
                <span class="text-carbon-50 text-xs inline-block relative top-[-1px]">&vert;</span>
                <button
                  data-test-deselect-all
                  {{on "click" this.onDeselectAll}}
                  class="btn btn--link !text-gem-60 btn--small font-bold"
                  type="button"
                >
                  Deselect all
                </button>
              {{/if}}
            </div>
          {{/if}}

          <ul class="ember-power-select-options overflow-auto">
            {{#each this.filteredOptions as |option|}}
              <li
                data-test-filter-option={{option.value}}
                {{on "click" (fn this.onOptionToggle option.value)}}
                class="ember-power-select-option cursor-pointer"
                tabindex="-1"
              >
                <div class="flex items-center">
                  <input type="checkbox" checked={{option.isSelected}} class="checkbox-fill-gem-50" tabindex="-1" />
                  <div class="truncate px-2 pt-[3px]">
                    <span>{{option.value}}</span>
                  </div>
                </div>
              </li>
            {{else}}
              <li class="text-left text-carbon-50 p-2">No results found</li>
            {{/each}}
          </ul>
        </div>
      </dropdown.Content>
    </BasicDropdown>
  </template>
}
